import Axios, { AxiosResponse } from 'axios';
import { CreateQuizDto, DailyTodos, GeneralResponse, KidAspectTodos, KidCreateOrUpdateDto, Todo, UserCreateDto } from './types';

interface ExtendedAxiosResponse<T = any> extends AxiosResponse<T> {
    ok: boolean;
}

Axios.defaults.withCredentials = true;
const baseURL = process.env.REACT_APP_API || window.location.origin;
const axios = Axios.create({ baseURL: new URL('/api', baseURL).href, withCredentials: true });

interface IPagination {
    page?: number;
    limit?: number;
}

interface IToken {
    accessToken: string;
    refreshToken: string;
}

export function token(): IToken {
    return JSON.parse(localStorage.getItem('token') || '{}');
}

export function setToken(token: IToken) {
    localStorage.setItem('token', JSON.stringify(token));
}

const refreshAccessToken = () =>
    Axios.get(new URL('/api/auth/refresh', baseURL).href, {
        headers: { Authorization: `Bearer ${token().refreshToken}` },
    })
        .then((res) => res.status === 200 && res.data && res.data.data)
        .catch((e) => (window.location.href = '/login'));

axios.interceptors.response.use(
    (response) => {
        let r = response as ExtendedAxiosResponse;
        if (response.status >= 200 && response.status < 300) {
            r.ok = true;
        }
        return r;
    },
    async function (error) {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry && originalRequest.url !== 'auth/login') {
            originalRequest._retry = true;
            const { accessToken, refreshToken } = await refreshAccessToken();
            setToken({ accessToken, refreshToken });
            if (accessToken) {
                originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
                return axios(originalRequest);
            } else {
                localStorage.clear();
                return Promise.reject('Invalid refresh token');
            }
        }
        return Promise.reject(error);
    },
);

axios.interceptors.request.use(async function (request) {
    try {
        const { accessToken } = token();
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
    } catch (error) {}
    return request;
});

export const api = {
    csrf: () => axios.get('csrf'),
    assessment: {
        getValid: (kidId: string) => {
            return axios.get<any, ExtendedAxiosResponse>('/assessment/getValid', { params: { kidId } });
        },
    },
    auth: {
        login: (username: string, password: string) => {
            return axios.post<any, ExtendedAxiosResponse<GeneralResponse<{ accessToken: string; refreshToken: string }>>>('auth/login', {
                username,
                password,
            });
        },
        profile: () => {
            return axios.get<any, ExtendedAxiosResponse>('/auth/profile');
        },
        verify: (challenge: string, code: string, password: string) => {
            return axios.post<any, ExtendedAxiosResponse>('/auth/verify', { challenge, code, password });
        },
        passwordReset: (oldPassword: string, newPassword: string) => {
            return axios.put<any, ExtendedAxiosResponse<GeneralResponse<any>>>('/auth/passwordReset', {
                newPassword,
                oldPassword,
            });
        },
        loginOtp: (username: string, code: string) => {
            return axios.post<any, ExtendedAxiosResponse<GeneralResponse<{ accessToken: string; refreshToken: string }>>>('auth/loginOtp', {
                username,
                code,
            });
        },
        requestOtpLogin: (username: string) => {
            return axios.post<any, ExtendedAxiosResponse<GeneralResponse<any>>>('/auth/requestOtpLogin', { username });
        },
    },
    kids: {
        search: (query?: { q?: string; submissionDate?: Date; age?: string; withAssessment?: boolean } & IPagination) => {
            return axios.get<any, ExtendedAxiosResponse>('/kids/search', { params: { ...query, _populate: ['parents'] } });
        },
        getAll: (query?: { nationalId?: string; q?: string; createdAt?: Date; age?: number } & IPagination) => {
            return axios.get<any, ExtendedAxiosResponse>('/kids', { params: { ...query, _populate: ['parents'] } });
        },
        getOne: (id: string) => {
            return axios.get<any, ExtendedAxiosResponse>(`/kids/${id}`, { params: { _populate: ['parents'] } });
        },
        create: (payload: KidCreateOrUpdateDto) => {
            return axios.post<any, ExtendedAxiosResponse>('/kids', payload);
        },
        createWithParent: (kid: KidCreateOrUpdateDto, parent: UserCreateDto) => {
            return axios.post<any, ExtendedAxiosResponse>('/kids/withParent', { kid, parent });
        },
        update: (id: string, payload: KidCreateOrUpdateDto) => {
            return axios.put<any, ExtendedAxiosResponse>(`/kids/${id}`, payload);
        },
        updateAvatar: (id: string, avatar: File) => {
            const form = new FormData();
            form.append('file', avatar);
            return axios.put<any, ExtendedAxiosResponse>(`/kids/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        },
    },
    quiz: {
        get: (id: string) => {
            return axios.get<any, ExtendedAxiosResponse>(`/quiz/${id}`, { params: { _populate: 'questions._id' } });
        },
        getCandidatesQuizForKid: (kidId: string) => {
            return axios.get<any, ExtendedAxiosResponse>('/quiz/candidates', { params: { kidId } });
        },
        submitQuizForKid: (id: string, body: CreateQuizDto) => {
            return axios.put<any, ExtendedAxiosResponse>('/quiz/submit', body, { params: { quizId: id } });
        },
        getQuizStats: (id: string) => {
            return axios.get<any, ExtendedAxiosResponse>(`/quiz/quizStats`, { params: { kidId: id } });
        },
        getTotalScore: (...ids: string[]) => {
            return axios.get<any, ExtendedAxiosResponse>(`/quiz/totalScore`, { params: { kids: ids } });
        },
        getTotalTimedAverageScore: (span: string, start: Date, end: Date, kidId?: string) => {
            return axios.get<any, ExtendedAxiosResponse>(`/quiz/timedAverage/${span}`, { params: { start: start, end: end, kidId } });
        },
    },
    todo: {
        getMostImportant: (params: IPagination & { status: 'done' | 'todo' | 'expired' }) => {
            return axios.get<any, ExtendedAxiosResponse<GeneralResponse<Todo[]>>>(`/todos/getMostImportant`, {
                params,
            });
        },
        updateTodoSetDone: (todoId: string) => {
            return axios.put<any, ExtendedAxiosResponse<GeneralResponse<any>>>(`/todos/setComplete`, { todoId });
        },
        forKid: (kidId: string) => {
            return axios.get<any, ExtendedAxiosResponse<GeneralResponse<KidAspectTodos[]>>>(`/todos/forKid`, { params: { kidId } });
        },
        groupedByTodoActionDaily: (date: Date) => {
            return axios.get<any, ExtendedAxiosResponse<GeneralResponse<DailyTodos[]>>>(`/todos/groupedByTodoActionDaily`, {
                params: { date },
            });
        },
        countByStatus: (kidId: string) => {
            return axios.get<any, ExtendedAxiosResponse<GeneralResponse<{ _id: string; count: number }[]>>>('/todos/countByStatus', {
                params: { kidId },
            });
        },
        countByAspect: (kidId: string) => {
            return axios.get<any, ExtendedAxiosResponse<GeneralResponse<{ _id: string; count: number }[]>>>('/todos/countByAspect', {
                params: { kidId },
            });
        },
    },
};
